/**
 * Aim assist (D-247, user feedback "near impossible to fight small ships"). Pure, allocation-free helpers
 * shared by the weapons step (which turns the guns) and the HUD (which shows the assist circle and state).
 *
 * The assist only aims the guns: when the target's lead point lies within the assist cone of the muzzle,
 * the shot direction turns toward it by at most `gimbal × weight × level`, where the weight is 1 near the
 * centre (inside `fullWithin × cone`) and eases smoothly to 0 at the cone edge. Projectiles then fly real
 * ballistics. The base weapons gimbal (spec §6) still snaps inside its own small cone at every level.
 * Player and AI run the same code; each ship's strength is `Weapons.assist` (the player's comes from the
 * Aim assist setting, AI pilots fly at Standard).
 */
import type { AimAssistData } from '../data/schemas'

/**
 * Weight 0..1 of the assist for a lead point `angle` radians off the muzzle: 1 inside `fullWithin × cone`,
 * a smoothstep down to 0 at `cone`, 0 beyond it.
 */
export function assistWeight(angle: number, cone: number, fullWithin: number): number {
  if (cone <= 0 || angle >= cone) return 0
  const inner = cone * fullWithin
  if (angle <= inner) return 1
  const u = (cone - angle) / (cone - inner)
  return u * u * (3 - 2 * u)
}

/**
 * How far (radians) the guns turn toward a lead point `angle` radians off the muzzle, for assist strength
 * `level` (0 = Off). Never more than `angle` (the guns stop on the lead point), and never more than the
 * assist gimbal × level. Inside the base gimbal the guns snap fully at any level.
 */
export function assistCorrection(angle: number, baseGimbal: number, a: AimAssistData, level: number): number {
  if (!(angle > 0)) return 0
  if (angle <= baseGimbal) return angle
  if (level <= 0) return 0
  const max = a.gimbal * level * assistWeight(angle, a.cone, a.fullWithin)
  return max >= angle ? angle : max
}

/**
 * Rotates the unit vector `d` toward the unit vector (tx, ty, tz) by `turn` radians (in place), in the
 * plane the two span. `angle` is the angle between them (from the caller's dot product).
 */
export function rotateToward(d: { x: number; y: number; z: number }, tx: number, ty: number, tz: number, angle: number, turn: number): void {
  if (turn <= 0 || angle <= 0) return
  if (turn >= angle) {
    d.x = tx
    d.y = ty
    d.z = tz
    return
  }
  // Slerp between d and t: sin((a − turn)) / sin(a) · d + sin(turn) / sin(a) · t.
  const s = Math.sin(angle)
  if (s < 1e-9) return
  const wa = Math.sin(angle - turn) / s
  const wb = Math.sin(turn) / s
  const x = d.x * wa + tx * wb
  const y = d.y * wa + ty * wb
  const z = d.z * wa + tz * wb
  const l = Math.sqrt(x * x + y * y + z * z)
  d.x = x / l
  d.y = y / l
  d.z = z / l
}
